/**
 * Simulation Diagnostics - Common Failure Evaluator
 *
 * Evaluates simulation inputs against known failure patterns to provide
 * actionable diagnosis before or after a simulation fails. Add new checkers
 * as you discover common failure modes.
 */

import { AppError, StructuredError } from '../../types/error.d';
import { extractServiceDate } from '../../utils/dateTimeUtils';

export interface DiagnosticResult {
  id: string;
  severity: 'error' | 'warning';
  what: string;
  why: string;
  howToFix: string;
}

export interface DiagnosticContext {
  rides: any[];
  fleet: any;
  /** When post-failure: the raw error message from API/backend */
  errorMessage?: string;
}

export type DiagnosticChecker = (ctx: DiagnosticContext) => DiagnosticResult | null;

// ---------------------------------------------------------------------------
// Common Failure Checkers - Add new ones as you discover patterns
// ---------------------------------------------------------------------------

/**
 * Fleet/Passenger mismatch: No standard seats for standard passengers,
 * or no wheelchair seats for wheelchair passengers.
 * This often causes backend to return null/empty results (AttributeError).
 */
function checkFleetPassengerMismatch(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;

  if (!rides?.length || !fleet?.vehicles?.length) return null;

  const hasStandardPassengers = rides.some((ride: any) =>
    (ride.passengers || []).some(
      (p: any) => (p.type === 'standard' || p.type === 'Standard') && (p.number_of_passengers ?? p.numberOfPassengers ?? 0) > 0
    )
  );
  const hasWheelchairPassengers = rides.some((ride: any) =>
    (ride.passengers || []).some(
      (p: any) => (p.type === 'wheelchair' || p.type === 'Wheelchair') && (p.number_of_passengers ?? p.numberOfPassengers ?? 0) > 0
    )
  );

  let totalStandardSeats = 0;
  let totalWheelchairSeats = 0;

  for (const vc of fleet.vehicles) {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    const count = vc.number_of_vehicles ?? vc.numberOfVehicles ?? 1;
    for (const cap of capacity) {
      const type = (cap.type ?? '').toLowerCase();
      const num = cap.number ?? 0;
      if (type === 'standard') totalStandardSeats += num * count;
      else if (type === 'wheelchair') totalWheelchairSeats += num * count;
    }
  }

  if (hasStandardPassengers && totalStandardSeats === 0) {
    return {
      id: 'no-standard-seats',
      severity: 'error',
      what: 'No trips can be delivered – fleet has no standard seats',
      why: 'All trips require standard passengers, but your fleet has no vehicles with standard seats. The simulation cannot assign any trips.',
      howToFix: 'Add vehicles with standard seats to your fleet, or ensure at least one vehicle type has standard capacity.',
    };
  }

  if (hasWheelchairPassengers && totalWheelchairSeats === 0) {
    return {
      id: 'no-wheelchair-seats',
      severity: 'error',
      what: 'No trips can be delivered – fleet has no wheelchair seats',
      why: 'Some trips require wheelchair passengers, but your fleet has no vehicles with wheelchair-accessible seats.',
      howToFix: 'Add vehicles with wheelchair seats to your fleet.',
    };
  }

  return null;
}

/**
 * Insufficient capacity: More passenger demand than fleet can handle.
 * May cause high unassigned rates (warning, not block).
 */
function checkInsufficientCapacity(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  let totalStandardSeats = 0;
  let totalWheelchairSeats = 0;
  let standardDemand = 0;
  let wheelchairDemand = 0;

  for (const vc of fleet.vehicles) {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    const count = vc.number_of_vehicles ?? vc.numberOfVehicles ?? 1;
    for (const cap of capacity) {
      const type = (cap.type ?? '').toLowerCase();
      const num = cap.number ?? 0;
      if (type === 'standard') totalStandardSeats += num * count;
      else if (type === 'wheelchair') totalWheelchairSeats += num * count;
    }
  }

  for (const ride of rides) {
    for (const p of ride.passengers ?? []) {
      const type = (p.type ?? '').toLowerCase();
      const num = p.number_of_passengers ?? p.numberOfPassengers ?? 0;
      if (type === 'standard') standardDemand += num;
      else if (type === 'wheelchair') wheelchairDemand += num;
    }
  }

  if (standardDemand > totalStandardSeats * 2 || wheelchairDemand > totalWheelchairSeats * 2) {
    return {
      id: 'insufficient-capacity',
      severity: 'warning',
      what: 'Fleet capacity may be insufficient for demand',
      why: `You have ${standardDemand} standard passenger requests and ${wheelchairDemand} wheelchair requests, but fleet capacity is limited. Expect many unassigned trips.`,
      howToFix: 'Add more vehicles or increase capacity to improve assignment rates.',
    };
  }

  return null;
}

/**
 * Empty rides: No rides to simulate.
 */
function checkEmptyRides(ctx: DiagnosticContext): DiagnosticResult | null {
  if (!ctx.rides?.length) {
    return {
      id: 'empty-rides',
      severity: 'error',
      what: 'No rides to simulate',
      why: 'The ride layer or selection has no trips.',
      howToFix: 'Select a ride layer with trips, or ensure your ride data is loaded correctly.',
    };
  }
  return null;
}

/**
 * Empty fleet: No vehicles to assign trips to.
 */
function checkEmptyFleet(ctx: DiagnosticContext): DiagnosticResult | null {
  if (!ctx.fleet?.vehicles?.length) {
    return {
      id: 'empty-fleet',
      severity: 'error',
      what: 'No vehicles in fleet',
      why: 'Your fleet has no vehicles, or all vehicles were filtered out (e.g. no on-demand capability).',
      howToFix: 'Add vehicles to your fleet and ensure they have on-demand operating periods.',
    };
  }
  return null;
}

/**
 * Invalid coordinates: Rides with null, NaN, or out-of-bounds lat/lng.
 */
function checkInvalidCoordinates(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides } = ctx;
  if (!rides?.length) return null;

  const invalidRides: number[] = [];
  rides.forEach((ride: any, idx: number) => {
    const fromLat = ride.from_latitude ?? ride.fromLatitude;
    const fromLng = ride.from_longitude ?? ride.fromLongitude;
    const toLat = ride.to_latitude ?? ride.toLatitude;
    const toLng = ride.to_longitude ?? ride.toLongitude;
    const invalid =
      fromLat == null || isNaN(fromLat) || fromLat < -90 || fromLat > 90 ||
      fromLng == null || isNaN(fromLng) || fromLng < -180 || fromLng > 180 ||
      toLat == null || isNaN(toLat) || toLat < -90 || toLat > 90 ||
      toLng == null || isNaN(toLng) || toLng < -180 || toLng > 180;
    if (invalid) invalidRides.push(idx + 1);
  });

  if (invalidRides.length > 0) {
    const sample = invalidRides.slice(0, 5).join(', ');
    const more = invalidRides.length > 5 ? ` (and ${invalidRides.length - 5} more)` : '';
    return {
      id: 'invalid-coordinates',
      severity: 'error',
      what: 'Some rides have invalid or missing coordinates',
      why: `Ride(s) at index ${sample}${more} have null, NaN, or out-of-bounds latitude/longitude. Valid ranges: lat -90 to 90, lng -180 to 180.`,
      howToFix: 'Check your ride layer data. Ensure all trips have valid origin and destination coordinates.',
    };
  }
  return null;
}

/**
 * Missing request time: Rides without request_time needed for scheduling.
 */
function checkMissingRequestTime(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides } = ctx;
  if (!rides?.length) return null;

  const missing = rides.filter(
    (r: any) => !r.request_time && !r.requestTime && !r.time
  );
  if (missing.length > 0) {
    const pct = Math.round((missing.length / rides.length) * 100);
    return {
      id: 'missing-request-time',
      severity: 'error',
      what: 'Some rides have no request time',
      why: `${missing.length} of ${rides.length} rides (${pct}%) have no request_time. The backend needs this for scheduling.`,
      howToFix: 'Ensure your ride data includes request_time (or time) for each trip. Use ISO format (e.g. 2024-01-15T08:00:00).',
    };
  }
  return null;
}

/**
 * Rides missing passengers: Rides without passenger info.
 */
function checkRidesMissingPassengers(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides } = ctx;
  if (!rides?.length) return null;

  const missing = rides.filter(
    (r: any) => !r.passengers?.length || r.passengers.every((p: any) => (p.number_of_passengers ?? p.numberOfPassengers ?? 0) === 0)
  );
  if (missing.length > 0) {
    return {
      id: 'rides-missing-passengers',
      severity: 'error',
      what: 'Some rides have no passenger information',
      why: `${missing.length} ride(s) have no passengers or zero passenger count. Each ride must specify passenger type (standard/wheelchair) and count.`,
      howToFix: 'Ensure every ride has a passengers array with at least one entry (type and number_of_passengers).',
    };
  }
  return null;
}

/**
 * Unknown passenger types: Rides with passenger types other than standard/wheelchair.
 */
function checkRidesInvalidPassengerType(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides } = ctx;
  if (!rides?.length) return null;

  const validTypes = ['standard', 'wheelchair'];
  const invalid: string[] = [];
  rides.forEach((r: any) => {
    (r.passengers || []).forEach((p: any) => {
      const t = (p.type ?? '').toLowerCase();
      if (t && !validTypes.includes(t)) invalid.push(t);
    });
  });
  const unique = Array.from(new Set(invalid));
  if (unique.length > 0) {
    return {
      id: 'rides-invalid-passenger-type',
      severity: 'warning',
      what: 'Some rides use unknown passenger types',
      why: `Passenger type(s) "${unique.join(', ')}" are not standard or wheelchair. The backend may not handle them correctly.`,
      howToFix: 'Use only "standard" or "wheelchair" for passenger types. Map other types to the closest supported type.',
    };
  }
  return null;
}

/**
 * Zero capacity vehicles: Vehicles that cannot carry any passengers.
 */
function checkZeroCapacityVehicles(ctx: DiagnosticContext): DiagnosticResult | null {
  const { fleet } = ctx;
  if (!fleet?.vehicles?.length) return null;

  const zeroCap: number[] = [];
  fleet.vehicles.forEach((vc: any, idx: number) => {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    let total = 0;
    const count = vc.number_of_vehicles ?? vc.numberOfVehicles ?? 1;
    capacity.forEach((cap: any) => {
      total += (cap.number ?? 0) * count;
    });
    if (total === 0) zeroCap.push(idx + 1);
  });

  if (zeroCap.length > 0) {
    return {
      id: 'zero-capacity-vehicles',
      severity: 'error',
      what: 'Some vehicles have zero total capacity',
      why: `Vehicle(s) at index ${zeroCap.join(', ')} have no standard or wheelchair seats. They cannot carry any passengers.`,
      howToFix: 'Set standardSeats and/or wheelchairSeats to at least 1 for each vehicle type.',
    };
  }
  return null;
}

/**
 * Wheelchair demand exceeds capacity: More wheelchair passengers than seats.
 */
function checkWheelchairDemandExceedsCapacity(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  let wheelchairSeats = 0;
  let wheelchairDemand = 0;

  fleet.vehicles.forEach((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    const count = vc.number_of_vehicles ?? vc.numberOfVehicles ?? 1;
    capacity.forEach((cap: any) => {
      if ((cap.type ?? '').toLowerCase() === 'wheelchair') {
        wheelchairSeats += (cap.number ?? 0) * count;
      }
    });
  });

  rides.forEach((r: any) => {
    (r.passengers || []).forEach((p: any) => {
      if ((p.type ?? '').toLowerCase() === 'wheelchair') {
        wheelchairDemand += p.number_of_passengers ?? p.numberOfPassengers ?? 0;
      }
    });
  });

  if (wheelchairDemand > 0 && wheelchairDemand > wheelchairSeats) {
    return {
      id: 'wheelchair-demand-exceeds-capacity',
      severity: 'warning',
      what: 'Wheelchair demand exceeds available seats',
      why: `You have ${wheelchairDemand} wheelchair passenger requests but only ${wheelchairSeats} wheelchair seats. Some wheelchair users will be unassigned.`,
      howToFix: 'Add more vehicles with wheelchair seats, or reduce wheelchair passenger demand.',
    };
  }
  return null;
}

/**
 * Standard demand exceeds capacity: Far more standard passengers than seats.
 */
function checkStandardDemandExceedsCapacity(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  let standardSeats = 0;
  let standardDemand = 0;

  fleet.vehicles.forEach((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    const count = vc.number_of_vehicles ?? vc.numberOfVehicles ?? 1;
    capacity.forEach((cap: any) => {
      if ((cap.type ?? '').toLowerCase() === 'standard') {
        standardSeats += (cap.number ?? 0) * count;
      }
    });
  });

  rides.forEach((r: any) => {
    (r.passengers || []).forEach((p: any) => {
      if ((p.type ?? '').toLowerCase() === 'standard') {
        standardDemand += p.number_of_passengers ?? p.numberOfPassengers ?? 0;
      }
    });
  });

  if (standardDemand > standardSeats * 3) {
    return {
      id: 'standard-demand-exceeds-capacity',
      severity: 'warning',
      what: 'Standard demand far exceeds fleet capacity',
      why: `You have ${standardDemand} standard passenger requests but only ${standardSeats} standard seats. Expect a high unassigned rate.`,
      howToFix: 'Add more vehicles with standard seats to improve assignment rates.',
    };
  }
  return null;
}

/**
 * Invalid passenger counts: Rides where number_of_passengers is NaN or non-finite.
 * This happens when the source layer has non-numeric values (e.g. "Yes", "N/A")
 * in the standard_seat or wheelchair columns.
 */
function checkPassengerCountValidity(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides } = ctx;
  if (!rides?.length) return null;

  const invalidRides = rides.filter((r: any) =>
    (r.passengers || []).some((p: any) => {
      const count = p.number_of_passengers ?? p.numberOfPassengers;
      return count === undefined || count === null || !isFinite(count) || isNaN(count);
    })
  );

  if (invalidRides.length > 0) {
    return {
      id: 'invalid-passenger-counts',
      severity: 'error',
      what: 'Rides have non-numeric passenger count values',
      why: `${invalidRides.length} ride(s) have invalid values in their standard or wheelchair passenger count fields. These must be whole numbers, but text or non-numeric values were found (e.g. "Yes", "N/A").`,
      howToFix: 'Open your ride layer and ensure the standard passenger and wheelchair fields contain whole numbers (e.g. 1, 2, 3). Remove or replace any text values in those columns.',
    };
  }

  return null;
}

/**
 * Single ride: Only one ride in simulation (may produce odd results).
 */
function checkSingleRide(ctx: DiagnosticContext): DiagnosticResult | null {
  if (ctx.rides?.length === 1) {
    return {
      id: 'single-ride',
      severity: 'warning',
      what: 'Only one ride in simulation',
      why: 'Simulations with a single ride may produce limited or unexpected results.',
      howToFix: 'Consider adding more rides for more meaningful simulation output.',
    };
  }
  return null;
}

/**
 * Vehicle locations at origin: Start/end addresses may be invalid (0,0).
 */
function checkVehicleLocationsAtOrigin(ctx: DiagnosticContext): DiagnosticResult | null {
  const { fleet } = ctx;
  if (!fleet?.vehicles?.length) return null;

  let atOrigin = 0;
  fleet.vehicles.forEach((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    const shifts = vehicle.shifts ?? [];
    shifts.forEach((shift: any) => {
      const start = shift.start_address ?? shift.startAddress;
      const end = shift.end_address ?? shift.endAddress;
      if (
        (start?.latitude === 0 && start?.longitude === 0) ||
        (end?.latitude === 0 && end?.longitude === 0)
      ) {
        atOrigin++;
      }
    });
  });

  if (atOrigin > 0) {
    return {
      id: 'vehicle-locations-at-origin',
      severity: 'warning',
      what: 'Some vehicle locations may be invalid',
      why: `${atOrigin} vehicle shift(s) have start or end address at (0, 0). This often means the location point was not found on the map.`,
      howToFix: 'Ensure vehicle start and end locations reference valid points on your map with correct coordinates.',
    };
  }
  return null;
}

/**
 * Break locations at origin: Any vehicle break whose break_addresses are
 * missing or sit at (0, 0). Null Island is ~16,000km from any real depot,
 * makes the shift infeasible, and silently zeroes the solver output — which
 * surfaces to the user as 100% unmatched rides with no error.
 *
 * This is a hard error because the mapper layer should already have
 * substituted the shift start address; hitting this means even the fallback
 * gave up (typically because the shift start_address itself is invalid).
 */
function checkBreakLocationsAtOrigin(ctx: DiagnosticContext): DiagnosticResult | null {
  const { fleet } = ctx;
  if (!fleet?.vehicles?.length) return null;

  let badBreaks = 0;
  fleet.vehicles.forEach((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    (vehicle.shifts ?? []).forEach((shift: any) => {
      (shift.breaks ?? []).forEach((br: any) => {
        const addresses = br.break_addresses ?? br.breakAddresses ?? [];
        if (!Array.isArray(addresses) || addresses.length === 0) {
          badBreaks++;
          return;
        }
        const hasReal = addresses.some((a: any) => {
          const lat = Number(a?.latitude);
          const lng = Number(a?.longitude);
          return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
        });
        if (!hasReal) badBreaks++;
      });
    });
  });

  if (badBreaks > 0) {
    return {
      id: 'break-locations-at-origin',
      severity: 'error',
      what: 'Vehicle breaks are missing a real location',
      why: `${badBreaks} break(s) have no break_addresses or sit at (0, 0). The solver will treat these breaks as occurring ~16,000km from the depot and return no assignments, so every ride will be unmatched.`,
      howToFix: 'Open the fleet, ensure each vehicle\'s start location resolves to a real point on the map, then re-save. Breaks default to the shift start address — if you see this error, the start address itself is invalid.',
    };
  }
  return null;
}

/**
 * Rides outside fleet hours: Ride request times fall outside all vehicle operating shifts.
 * Checks whether each ride's request_time falls within any vehicle's shift (start_time to end_time).
 */
function checkRidesOutsideFleetHours(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  const shifts: { start: number; end: number }[] = [];
  fleet.vehicles.forEach((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    (vehicle.shifts ?? []).forEach((shift: any) => {
      const startStr = shift.start_time ?? shift.startTime;
      const endStr = shift.end_time ?? shift.endTime;
      if (startStr && endStr) {
        const start = new Date(startStr).getTime();
        const end = new Date(endStr).getTime();
        if (!isNaN(start) && !isNaN(end)) shifts.push({ start, end });
      }
    });
  });
  if (shifts.length === 0) return null;

  const outside: number[] = [];
  rides.forEach((r: any, idx: number) => {
    const t = r.request_time ?? r.requestTime ?? r.time;
    if (!t) return;
    const rideTime = new Date(t).getTime();
    if (isNaN(rideTime)) return;
    const withinAny = shifts.some((s) => rideTime >= s.start && rideTime <= s.end);
    if (!withinAny) outside.push(idx + 1);
  });

  if (outside.length > 0) {
    const sample = outside.slice(0, 5).join(', ');
    const more = outside.length > 5 ? ` (and ${outside.length - 5} more)` : '';
    return {
      id: 'rides-outside-fleet-hours',
      severity: 'warning',
      what: 'Some rides fall outside fleet operating hours',
      why: `Ride(s) at index ${sample}${more} have request times outside all vehicle shift hours. These trips may not be assignable.`,
      howToFix: 'Ensure ride request times fall within vehicle operating periods, or extend fleet shift hours to cover all ride times.',
    };
  }
  return null;
}

/**
 * Ride-fleet date mismatch: Ride request dates don't match fleet service date.
 * Extracts date (YYYY-MM-DD) from ride request_time and fleet uatMetadata.serviceDate or first shift.
 */
function checkRideFleetDateMismatch(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  const rideDateStrings: string[] = [];
  rides.forEach((r: any) => {
    const t = r.request_time ?? r.requestTime ?? r.time;
    if (t) {
      try {
        const datePart = extractServiceDate(String(t));
        if (datePart && rideDateStrings.indexOf(datePart) === -1) {
          rideDateStrings.push(datePart);
        }
      } catch {
        // Ignore malformed timestamps in diagnostics.
      }
    }
  });
  if (rideDateStrings.length === 0) return null;

  let fleetDate: string | null = null;
  if (fleet.uatMetadata?.serviceDate) {
    try {
      fleetDate = extractServiceDate(String(fleet.uatMetadata.serviceDate));
    } catch {
      fleetDate = null;
    }
  }
  if (!fleetDate && fleet.vehicles?.[0]?.vehicle?.shifts?.[0]?.start_time) {
    const t = fleet.vehicles[0].vehicle.shifts[0].start_time;
    try {
      fleetDate = extractServiceDate(String(t));
    } catch {
      fleetDate = null;
    }
  }
  if (!fleetDate) return null;

  const mismatched = rideDateStrings.filter((d) => d !== fleetDate);
  if (mismatched.length > 0) {
    return {
      id: 'ride-fleet-date-mismatch',
      severity: 'warning',
      what: 'Ride dates may not match fleet service date',
      why: `Ride request times use date(s) ${rideDateStrings.join(', ')}, but fleet service date is ${fleetDate}. Time windows may not align correctly.`,
      howToFix: 'Ensure ride request times and fleet service date use the same day. Check fleet metadata and ride layer data.',
    };
  }
  return null;
}

/**
 * Mixed passengers, no mixed vehicle: Rides need both standard and wheelchair but no vehicle has both.
 */
function checkMixedPassengersNoMixedVehicle(ctx: DiagnosticContext): DiagnosticResult | null {
  const { rides, fleet } = ctx;
  if (!rides?.length || !fleet?.vehicles?.length) return null;

  const hasMixedRides = rides.some((r: any) => {
    const passengers = r.passengers ?? [];
    const hasStandard = passengers.some((p: any) => (p.type ?? '').toLowerCase() === 'standard' && (p.number_of_passengers ?? p.numberOfPassengers ?? 0) > 0);
    const hasWheelchair = passengers.some((p: any) => (p.type ?? '').toLowerCase() === 'wheelchair' && (p.number_of_passengers ?? p.numberOfPassengers ?? 0) > 0);
    return hasStandard && hasWheelchair;
  });

  if (!hasMixedRides) return null;

  const hasMixedVehicle = fleet.vehicles.some((vc: any) => {
    const vehicle = vc.vehicle ?? vc;
    const capacity = vehicle.capacity ?? [];
    const hasStandard = capacity.some((c: any) => (c.type ?? '').toLowerCase() === 'standard' && (c.number ?? 0) > 0);
    const hasWheelchair = capacity.some((c: any) => (c.type ?? '').toLowerCase() === 'wheelchair' && (c.number ?? 0) > 0);
    return hasStandard && hasWheelchair;
  });

  if (!hasMixedVehicle) {
    return {
      id: 'mixed-passengers-no-mixed-vehicle',
      severity: 'warning',
      what: 'Some rides need both seat types but no vehicle has both',
      why: 'At least one ride requires both standard and wheelchair passengers, but no vehicle in the fleet has both seat types.',
      howToFix: 'Add at least one vehicle type with both standard and wheelchair capacity.',
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Error patterns that suggest "empty/null results" from backend
// When we see these, run diagnostics to try to explain the root cause
// ---------------------------------------------------------------------------
const EMPTY_RESULT_ERROR_PATTERNS = [
  'NoneType',
  "has no attribute 'get'",
  'get_assigned_rides',
  'on_demand_bus_result',
  'solution',
  'AttributeError',
  'no attribute',
  'KeyError',
  'IndexError',
  'empty result',
  'no solution',
  'assigned_rides',
];

function suggestsEmptyBackendResult(errorMessage: string): boolean {
  const lower = (errorMessage || '').toLowerCase();
  return EMPTY_RESULT_ERROR_PATTERNS.some((p) => lower.includes(p.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const PRE_FLIGHT_CHECKERS: DiagnosticChecker[] = [
  checkEmptyRides,
  checkEmptyFleet,
  checkInvalidCoordinates,
  checkMissingRequestTime,
  checkPassengerCountValidity,
  checkRidesMissingPassengers,
  checkZeroCapacityVehicles,
  checkFleetPassengerMismatch,
  checkRidesInvalidPassengerType,
  checkInsufficientCapacity,
  checkWheelchairDemandExceedsCapacity,
  checkStandardDemandExceedsCapacity,
  checkMixedPassengersNoMixedVehicle,
  checkRideFleetDateMismatch,
  checkRidesOutsideFleetHours,
  checkSingleRide,
  checkVehicleLocationsAtOrigin,
  checkBreakLocationsAtOrigin,
];

const POST_FAILURE_CHECKERS: DiagnosticChecker[] = [
  checkBreakLocationsAtOrigin,
  checkFleetPassengerMismatch,
  checkRidesOutsideFleetHours,
  checkInvalidCoordinates,
  checkMissingRequestTime,
  checkPassengerCountValidity,
  checkRidesMissingPassengers,
  checkZeroCapacityVehicles,
  checkInsufficientCapacity,
  checkWheelchairDemandExceedsCapacity,
  checkStandardDemandExceedsCapacity,
];

/**
 * Run pre-flight diagnostics before starting a simulation.
 * Returns errors that should block, and warnings for info.
 */
export function runPreFlightDiagnostics(rides: any[], fleet: any): {
  errors: DiagnosticResult[];
  warnings: DiagnosticResult[];
} {
  const ctx: DiagnosticContext = { rides, fleet };
  const errors: DiagnosticResult[] = [];
  const warnings: DiagnosticResult[] = [];

  for (const checker of PRE_FLIGHT_CHECKERS) {
    const result = checker(ctx);
    if (result) {
      if (result.severity === 'error') errors.push(result);
      else warnings.push(result);
    }
  }

  return { errors, warnings };
}

/**
 * Run post-failure diagnostics when a simulation fails.
 * Use when the error suggests empty/null backend results.
 * Returns the first matching diagnostic, if any.
 */
export function runPostFailureDiagnostics(
  rides: any[],
  fleet: any,
  errorMessage: string
): DiagnosticResult | null {
  if (!suggestsEmptyBackendResult(errorMessage)) return null;

  const ctx: DiagnosticContext = { rides, fleet, errorMessage };

  for (const checker of POST_FAILURE_CHECKERS) {
    const result = checker(ctx);
    if (result) return result;
  }

  return null;
}

/**
 * Augment an AppError with a diagnosed cause when we can explain the failure.
 */
export function augmentErrorWithDiagnosis(
  appError: AppError,
  diagnosis: DiagnosticResult
): AppError {
  const orig = appError.structuredError;
  const augmented: StructuredError = {
    what: diagnosis.what,
    why: `${diagnosis.why} (Backend error: ${orig.why})`,
    howToFix: diagnosis.howToFix,
    category: 'Validation',
    details: { ...orig.details, originalError: orig, diagnosis },
  };
  return new AppError(augmented);
}
